import { Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { Skeleton } from '@/components/ui/skeleton';
import { Toaster } from '@/components/ui/sonner';
import { UsersAdminPage } from '@/features/admin/UsersAdminPage';
import { LoginPage } from '@/features/auth/LoginPage';
import { useMe } from '@/features/auth/api';
import { CustomersPage } from '@/features/customers/CustomersPage';
import { DraftListPage } from '@/features/invoices/compose/DraftListPage';
import { InvoiceComposerPage } from '@/features/invoices/compose/InvoiceComposerPage';
import { NewInvoicePage } from '@/features/invoices/compose/NewInvoicePage';
import { OutboundInvoicePage } from '@/features/invoices/outbound/OutboundInvoicePage';
import { OutboundListPage } from '@/features/invoices/outbound/OutboundListPage';
import { InvoiceDetailPage } from '@/features/invoices/InvoiceDetailPage';
import { InvoiceListPage } from '@/features/invoices/InvoiceListPage';
import { InvoicesLayout } from '@/features/invoices/InvoicesLayout';
import { SummaryPage } from '@/features/invoices/SummaryPage';
import { PrivacyPolicyPage } from '@/features/privacy/PrivacyPolicyPage';
import { ServerPrefsSync } from '@/features/settings/ServerPrefsSync';
import { SettingsPage } from '@/features/settings/SettingsPage';

export function App() {
  const me = useMe();

  return (
    <>
      <Toaster richColors closeButton position="bottom-center" />
      {(() => {
        if (me.isPending) return <BootSkeleton />;
        if (!me.data) {
          return (
            <Routes>
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="*" element={<LoginPage />} />
            </Routes>
          );
        }
        return (
          <>
            <ServerPrefsSync />
            <Routes>
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route element={<AppShell me={me.data} />}>
                <Route index element={<Navigate to="/invoices/summary" replace />} />
                <Route path="invoices" element={<InvoicesLayout />}>
                  <Route index element={<Navigate to="/invoices/summary" replace />} />
                  <Route path="summary" element={<SummaryPage />} />
                  {me.data.issuingEnabled && (
                    <>
                      <Route path="new" element={<NewInvoicePage />} />
                      <Route path="new/standard" element={<InvoiceComposerPage />} />
                      <Route path="new/standard-margin" element={<InvoiceComposerPage />} />
                      <Route path="drafts" element={<DraftListPage />} />
                      <Route path="drafts/:id" element={<InvoiceComposerPage />} />
                      <Route path="sent" element={<OutboundListPage />} />
                      <Route path="sent/:id" element={<OutboundInvoicePage />} />
                    </>
                  )}
                  <Route path="issued" element={<InvoiceListPage subject="SELLER" />} />
                  <Route path="issued/:ksefNumber" element={<InvoiceDetailPage />} />
                  <Route path="received" element={<InvoiceListPage subject="BUYER" />} />
                  <Route path="received/:ksefNumber" element={<InvoiceDetailPage />} />
                </Route>
                <Route path="settings" element={<SettingsPage />} />
                <Route path="customers" element={<CustomersPage />} />
                {me.data.role === 'ADMIN' && (
                  <Route path="admin/users" element={<UsersAdminPage />} />
                )}
                <Route path="*" element={<Navigate to="/invoices/summary" replace />} />
              </Route>
            </Routes>
          </>
        );
      })()}
    </>
  );
}

function BootSkeleton() {
  return (
    <div className="grid min-h-screen place-items-center bg-background">
      <div className="w-full max-w-md space-y-3 p-6">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </div>
  );
}
